import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Button, 
  Progress, 
  Alert, 
  Space, 
  Typography, 
  Descriptions, 
  Tag, 
  Spin,
  Modal,
  message 
} from 'antd';
import { 
  PlayCircleOutlined, 
  ReloadOutlined, 
  DeleteOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined 
} from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { RecognitionIndexStatusEnum } from '../enums/RecognitionIndexStatusEnum';
import { 
  useStartEventIndexingMutation,
  useGetEventIndexingStatusQuery,
  useGetEventCollectionStatsQuery,
  useDeleteEventCollectionMutation
} from '../services/nestjs-base-api/admin-api';
import { useAuth } from '../hooks/useAuth';
import dayjs from 'dayjs';

const { Text } = Typography;

interface IRecognitionIndexingStatusProps {
  eventId: number;
  eventName: string;
}

const RecognitionIndexingStatus: React.FunctionComponent<IRecognitionIndexingStatusProps> = ({
  eventId,
  eventName
}) => {
  const intl = useIntl();
  const { user } = useAuth();
  const [pollingEnabled, setPollingEnabled] = useState(false);

  // API hooks
  const [startIndexing, { isLoading: isStartingIndexing }] = useStartEventIndexingMutation();
  const [deleteCollection, { isLoading: isDeletingCollection }] = useDeleteEventCollectionMutation();
  
  const { 
    data: indexingStatus, 
    error: statusError, 
    isLoading: isLoadingStatus,
    refetch: refetchStatus
  } = useGetEventIndexingStatusQuery({
    jwtToken: user.jwtToken,
    eventId
  }, {
    pollingInterval: pollingEnabled ? 2000 : 0, // Poll every 2 seconds when indexing
    skip: !user.jwtToken
  });

  const { 
    data: collectionStats, 
    isLoading: isLoadingStats,
    refetch: refetchStats
  } = useGetEventCollectionStatsQuery({
    jwtToken: user.jwtToken,
    eventId
  }, {
    skip: !user.jwtToken
  });

  // Enable polling when indexing is in progress
  useEffect(() => {
    if (indexingStatus?.status === RecognitionIndexStatusEnum.InProgress) {
      setPollingEnabled(true);
    } else {
      setPollingEnabled(false);
    }
  }, [indexingStatus?.status]);

  // Handle start indexing
  const handleStartIndexing = async () => {
    try {
      await startIndexing({
        jwtToken: user.jwtToken,
        eventId
      }).unwrap();
      
      message.success(intl.formatMessage({ 
        id: 'indexing.started.success',
        defaultMessage: 'Indexing started successfully'
      }));
      
      // Refresh status and start polling
      refetchStatus();
      setPollingEnabled(true);
    } catch (error) {
      message.error(intl.formatMessage({ 
        id: 'indexing.started.error',
        defaultMessage: 'Failed to start indexing'
      }));
    }
  };

  // Handle delete collection
  const handleDeleteCollection = () => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'collection.delete.confirm.title',
        defaultMessage: 'Delete Collection'
      }),
      content: intl.formatMessage({
        id: 'collection.delete.confirm.content',
        defaultMessage: 'Are you sure you want to delete the recognition collection? This will require re-indexing before searches can work again.'
      }),
      onOk: async () => {
        try {
          await deleteCollection({
            jwtToken: user.jwtToken,
            eventId
          }).unwrap();
          
          message.success(intl.formatMessage({ 
            id: 'collection.deleted.success',
            defaultMessage: 'Collection deleted successfully'
          }));
          
          refetchStatus();
          refetchStats();
        } catch (error) {
          message.error(intl.formatMessage({ 
            id: 'collection.deleted.error',
            defaultMessage: 'Failed to delete collection'
          }));
        }
      }
    });
  };

  // Get status tag color and icon
  const getStatusDisplay = (status?: RecognitionIndexStatusEnum) => {
    switch (status) {
      case RecognitionIndexStatusEnum.NotStarted:
        return { 
          color: 'default', 
          icon: <InfoCircleOutlined />, 
          text: intl.formatMessage({ id: 'indexing.status.notStarted', defaultMessage: 'Not Started' })
        };
      case RecognitionIndexStatusEnum.InProgress:
        return { 
          color: 'processing', 
          icon: <ClockCircleOutlined />, 
          text: intl.formatMessage({ id: 'indexing.status.inProgress', defaultMessage: 'In Progress' })
        };
      case RecognitionIndexStatusEnum.Completed:
        return { 
          color: 'success', 
          icon: <CheckCircleOutlined />, 
          text: intl.formatMessage({ id: 'indexing.status.completed', defaultMessage: 'Completed' })
        };
      case RecognitionIndexStatusEnum.Failed:
        return { 
          color: 'error', 
          icon: <ExclamationCircleOutlined />, 
          text: intl.formatMessage({ id: 'indexing.status.failed', defaultMessage: 'Failed' })
        };
      default:
        return { 
          color: 'default', 
          icon: <InfoCircleOutlined />, 
          text: intl.formatMessage({ id: 'indexing.status.unknown', defaultMessage: 'Unknown' })
        };
    }
  };

  const statusDisplay = getStatusDisplay(indexingStatus?.status);
  const needsReindexing = indexingStatus?.needsReindex || indexingStatus?.status === RecognitionIndexStatusEnum.NotStarted;
  const canStartIndexing = !isStartingIndexing && (
    indexingStatus?.status === RecognitionIndexStatusEnum.NotStarted ||
    indexingStatus?.status === RecognitionIndexStatusEnum.Failed ||
    needsReindexing
  );

  if (isLoadingStatus) {
    return (
      <Card 
        title={
          <FormattedMessage 
            id="recognition.indexing.title" 
            defaultMessage="Photo Recognition Indexing"
          />
        }
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <FormattedMessage 
          id="recognition.indexing.title" 
          defaultMessage="Photo Recognition Indexing"
        />
      }
      extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />}
            onClick={() => {
              refetchStatus();
              refetchStats();
            }}
            size="small"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <FormattedMessage id="common.refresh" defaultMessage="Refresh" />
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Status Overview */}
        <Descriptions column={1} bordered size="small">
          <Descriptions.Item 
            label={intl.formatMessage({ id: 'indexing.status', defaultMessage: 'Status' })}
          >
            <Tag color={statusDisplay.color} icon={statusDisplay.icon}>
              {statusDisplay.text}
            </Tag>
          </Descriptions.Item>
          
          {indexingStatus?.progress !== undefined && (
            <Descriptions.Item 
              label={intl.formatMessage({ id: 'indexing.progress', defaultMessage: 'Progress' })}
            >
              <Progress 
                percent={indexingStatus.progress} 
                status={indexingStatus.status === RecognitionIndexStatusEnum.Failed ? 'exception' : 'active'}
                size="small"
              />
              <Text type="secondary">
                {indexingStatus.processedItems || 0} / {indexingStatus.totalItems || 0} photos processed
              </Text>
            </Descriptions.Item>
          )}

          {indexingStatus?.startedAt && (
            <Descriptions.Item 
              label={intl.formatMessage({ id: 'indexing.startedAt', defaultMessage: 'Started At' })}
            >
              {dayjs(indexingStatus.startedAt).format('MMMM D, YYYY h:mm A')}
            </Descriptions.Item>
          )}

          {indexingStatus?.completedAt && (
            <Descriptions.Item 
              label={intl.formatMessage({ id: 'indexing.completedAt', defaultMessage: 'Completed At' })}
            >
              {dayjs(indexingStatus.completedAt).format('MMMM D, YYYY h:mm A')}
            </Descriptions.Item>
          )}

          {collectionStats?.exists && (
            <Descriptions.Item 
              label={intl.formatMessage({ id: 'collection.faceCount', defaultMessage: 'Indexed Faces' })}
            >
              {collectionStats.faceCount || 0}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Alerts and Messages */}
        {needsReindexing && (
          <Alert
            message={intl.formatMessage({ 
              id: 'indexing.reindexRequired.title',
              defaultMessage: 'Re-indexing Required'
            })}
            description={intl.formatMessage({ 
              id: 'indexing.reindexRequired.description',
              defaultMessage: 'Photos have been added, removed, or modified since the last indexing. Please re-index to enable photo recognition searches.'
            })}
            type="warning"
            showIcon
          />
        )}

        {indexingStatus?.status === RecognitionIndexStatusEnum.InProgress && (
          <Alert
            message={intl.formatMessage({ 
              id: 'indexing.inProgress.title',
              defaultMessage: 'Indexing in Progress'
            })}
            description={intl.formatMessage({ 
              id: 'indexing.inProgress.description',
              defaultMessage: 'Photo recognition indexing is currently running. This page will update automatically as progress is made.'
            })}
            type="info"
            showIcon
          />
        )}

        {indexingStatus?.error && (
          <Alert
            message={intl.formatMessage({ 
              id: 'indexing.error.title',
              defaultMessage: 'Indexing Error'
            })}
            description={indexingStatus.error}
            type="error"
            showIcon
          />
        )}

        {indexingStatus?.status === RecognitionIndexStatusEnum.Completed && !needsReindexing && (
          <Alert
            message={intl.formatMessage({ 
              id: 'indexing.completed.title',
              defaultMessage: 'Indexing Complete'
            })}
            description={intl.formatMessage({ 
              id: 'indexing.completed.description',
              defaultMessage: 'Photo recognition indexing is complete. Users can now search for photos using selfie uploads.'
            })}
            type="success"
            showIcon
          />
        )}

        {/* Action Buttons */}
        <Space>
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleStartIndexing}
            loading={isStartingIndexing}
            disabled={!canStartIndexing}
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            {needsReindexing ? (
              <FormattedMessage id="indexing.reindex" defaultMessage="Re-index Photos" />
            ) : (
              <FormattedMessage id="indexing.start" defaultMessage="Start Indexing" />
            )}
          </Button>

          {collectionStats?.exists && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDeleteCollection}
              loading={isDeletingCollection}
              disabled={indexingStatus?.status === RecognitionIndexStatusEnum.InProgress}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <FormattedMessage id="collection.delete" defaultMessage="Delete Collection" />
            </Button>
          )}
        </Space>
      </Space>
    </Card>
  );
};

export default RecognitionIndexingStatus;